// BALANCE: KYRS (left) and NUMA (right) talk it out loud and fix the schedule.
//
// It is a two-seat council (same server agents, same tools, same voices, same
// transcript as the Pantheon's COUNCIL): NUMA reads the dials, KYRS owns the
// schedule, and they hand the turn back and forth until one of them says
// BALANCED. The user can type or speak into the chat at any point; their line
// joins the conversation before the next speaker.
//
// Nothing they do is silent or permanent (Joel, 2026-10-18):
//  - CHANGES: every task change is logged by a database trigger (task_change_log)
//    with its before/after. This room tags its agents' rows with the session +
//    deity, and lists them with Approve / Revert / Change (Joel's own day + time).
//  - HISTORY: every BALANCE conversation is saved; tap one to reopen it.
//
// Laid over the river: KYRS at its left bank, NUMA at its right, the chat on
// the water between them.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { Check, History, ListChecks, MessageSquare, Pencil, Plus, Square, Undo2, X } from 'lucide-react-native';
import { useAuth } from '../auth/AuthProvider';
import { HallFigure } from '../v10/HallFigure';
import { HALL_CLIPS } from '../v10/hallClips';
import { makeStyles, useV10 } from '../v10/theme';
import { Medallion } from '../v10/ui';
import { Butterfly } from '../v10/ButterflyIcon';
import {
  approveChange,
  describeChange,
  editChangeSchedule,
  listBalanceSessions,
  listChanges,
  revertChange,
  tagChanges,
  whoMade,
  type BalanceSession,
  type TaskChange,
} from './changeLog';
import { CouncilComposer, CouncilTranscript } from './CouncilPanels';
import { seatName, useCouncil } from './useCouncil';

const LEFT = 'kyrs';
const RIGHT = 'numa';
/** NUMA reads the dials first, then they alternate. */
const ORDER = [RIGHT, LEFT];
const MAX_TURNS = 10;
const isBalanced = (text: string) => /\bBALANCED\b/.test(text);

export type BalancePanel = 'chat' | 'changes' | 'history';

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
};
const stamp = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export function BalanceRoom({
  visible,
  briefing,
  onClose,
  onWrote,
  startTalking = true,
  initialPanel = 'chat',
}: {
  visible: boolean;
  /** The opening line: real numbers + task ids. */
  briefing: string;
  onClose: () => void;
  /** A deity (or Joel, from the change list) changed tasks: re-read the river. */
  onWrote?: () => void;
  /** false = open to review history/changes without starting a conversation. */
  startTalking?: boolean;
  initialPanel?: BalancePanel;
}) {
  const t = useV10();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user?.id;
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [panel, setPanel] = useState<BalancePanel>(initialPanel);
  const [changes, setChanges] = useState<TaskChange[]>([]);
  const [sessions, setSessions] = useState<BalanceSession[]>([]);
  const [filter, setFilter] = useState<'pending' | 'talk' | 'all'>('pending');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<TaskChange | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);

  const reloadChanges = useCallback(async () => {
    if (userId) setChanges(await listChanges(userId));
  }, [userId]);
  const reloadSessions = useCallback(async () => {
    if (userId) setSessions(await listBalanceSessions(userId));
  }, [userId]);

  const onDeityWrote = useCallback(
    async (w: { sessionId: string; speaker: string; since: string }) => {
      if (userId) await tagChanges(userId, w.sessionId, w.speaker, w.since);
      onWrote?.();
      void reloadChanges();
    },
    [userId, onWrote, reloadChanges],
  );

  const c = useCouncil({
    active: visible,
    initialMembers: ORDER,
    roomName: `Balance ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`,
    alwaysSpeak: true,
    onWrote: onDeityWrote,
  });

  const talk = useCallback(async () => {
    if (!briefing) return;
    setPanel('chat');
    await c.send(briefing);
    await c.converse(ORDER, MAX_TURNS, (l) => isBalanced(l.content));
    void reloadChanges();
    void reloadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefing, reloadChanges, reloadSessions]);

  // Opening: post the briefing (both are named in it, so both answer), then keep them talking.
  const started = useRef(false);
  useEffect(() => {
    if (!visible) {
      started.current = false;
      return;
    }
    setPanel(initialPanel);
    void reloadChanges();
    void reloadSessions();
    if (started.current || !startTalking || !briefing) return;
    started.current = true;
    void talk();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, briefing]);

  // The briefing is for the deities (numbers + task ids); in the chat it reads as one short line.
  const shown = React.useMemo(
    () => ({ ...c, lines: c.lines.map((l) => (l.speaker === 'user' && l.content.startsWith('BALANCE.') ? { ...l, content: 'Balance it. Fix the schedule and get me back in the green.' } : l)) }),
    [c],
  );

  const close = () => {
    c.stop();
    setEditing(null);
    onClose();
  };

  const act = async (ch: TaskChange, fn: () => Promise<string | null>) => {
    setBusyId(ch.id);
    setErr(null);
    const e = await fn();
    setBusyId(null);
    if (e) setErr(e);
    onWrote?.();
    void reloadChanges();
  };

  if (!visible) return null;

  const pending = changes.filter((x) => x.status === 'pending');
  const listed = filter === 'pending' ? pending : filter === 'talk' ? changes.filter((x) => x.session_id && x.session_id === c.sessionId) : changes;

  const { w: W, h: H } = box;
  const composerH = 64;
  const wide = W >= 560;
  const figH = wide ? Math.max(120, H - composerH - 24) : Math.max(110, H * 0.36);
  const figW = (id: string) => figH * (HALL_CLIPS[id]?.aspect ?? 0.45);
  const sideL = wide ? figW(LEFT) + 8 : 8;
  const sideR = wide ? figW(RIGHT) + 8 : 8;
  const chatBottom = panel === 'chat' ? (wide ? composerH + 8 : composerH + figH + 20) : wide ? 8 : figH + 20;
  const sheet = { left: sideL, right: sideR, top: 42, bottom: chatBottom, backgroundColor: 'rgba(9,14,32,0.86)' };

  const figure = (id: string, side: 'left' | 'right') => {
    const set = HALL_CLIPS[id];
    const fw = figW(id);
    const speaking = c.speakingId === id;
    const thinking = c.thinking === id;
    return (
      <View pointerEvents="none" style={[styles.stand, { width: fw + 8, [side]: 0, bottom: wide ? 8 : panel === 'chat' ? composerH + 4 : 4 }]}>
        <View style={[styles.glow, { width: fw * 0.9, height: fw * 0.9, borderRadius: fw, top: figH * 0.22, opacity: speaking ? 0.6 : thinking ? 0.3 : 0 }]} />
        {set ? <HallFigure set={set} width={fw} height={figH} active={visible} /> : <View style={{ width: fw, height: figH }} />}
        <View style={styles.plate}>
          <Text style={[styles.name, speaking && { color: t.goldBright }]}>{seatName(id)}</Text>
          <Text style={styles.state}>{speaking ? 'speaking' : thinking ? 'thinking…' : id === RIGHT ? 'the dials' : 'the schedule'}</Text>
        </View>
      </View>
    );
  };

  const who = (ch: TaskChange) =>
    ch.speaker === 'claude' ? (
      <View style={styles.whoDot}>
        <Text style={styles.whoDotText}>C</Text>
      </View>
    ) : ch.speaker === 'syqi' ? (
      <View style={styles.whoDot}>
        <Butterfly size={13} color={t.goldBright} />
      </View>
    ) : ch.speaker ? (
      <Medallion id={ch.speaker} size={24} />
    ) : (
      <View style={styles.whoDot}>
        <Text style={styles.whoDotText}>?</Text>
      </View>
    );

  const statusColor = (s: TaskChange['status']) => (s === 'pending' ? t.goldBright : s === 'approved' ? t.onPace : s === 'reverted' ? t.lapsed : t.behind);

  const changesPanel = (
    <View style={[styles.sheet, sheet]}>
      <View style={styles.sheetHead}>
        <Text style={styles.sheetTitle}>CHANGES</Text>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {(
            [
              ['pending', `To review (${pending.length})`],
              ['talk', 'This talk'],
              ['all', 'All'],
            ] as const
          ).map(([k, label]) => (
            <Pressable key={k} onPress={() => setFilter(k)} style={[styles.chip, filter === k && styles.chipOn]}>
              <Text style={[styles.chipText, filter === k && styles.chipTextOn]}>{label}</Text>
            </Pressable>
          ))}
        </View>
      </View>
      {filter === 'pending' && pending.length > 1 ? (
        <View style={styles.bulkRow}>
          <Pressable
            style={[styles.btn, styles.btnApprove]}
            disabled={!!busyId}
            onPress={async () => {
              setBusyId('all');
              for (const ch of pending) await approveChange(ch);
              setBusyId(null);
              void reloadChanges();
            }}
          >
            <Check size={13} color={t.onGold} />
            <Text style={styles.btnTextDark}>Approve all {pending.length}</Text>
          </Pressable>
          <Pressable
            style={[styles.btn, confirmAll ? styles.btnDanger : styles.btnGhost]}
            disabled={!!busyId}
            onPress={async () => {
              if (!confirmAll) {
                setConfirmAll(true);
                return;
              }
              setConfirmAll(false);
              setBusyId('all');
              setErr(null);
              // Newest first, so stacked changes on one task unwind in order.
              for (const ch of pending) {
                const e = userId ? await revertChange(userId, ch) : 'Not signed in';
                if (e) {
                  setErr(e);
                  break;
                }
              }
              setBusyId(null);
              onWrote?.();
              void reloadChanges();
            }}
          >
            <Undo2 size={13} color={confirmAll ? '#fff' : t.ivory} />
            <Text style={confirmAll ? styles.btnTextLight : styles.btnText}>{confirmAll ? `Tap again: revert all ${pending.length}` : `Revert all ${pending.length}`}</Text>
          </Pressable>
        </View>
      ) : null}
      {err ? <Text style={styles.err}>{err}</Text> : null}
      <ScrollView contentContainerStyle={{ gap: 8, padding: 10 }}>
        {listed.length === 0 ? <Text style={styles.empty}>{filter === 'pending' ? 'Nothing waiting for you.' : 'No changes yet.'}</Text> : null}
        {listed.map((ch) => (
          <View key={ch.id} style={styles.card}>
            <View style={styles.cardHead}>
              {who(ch)}
              <Text style={styles.cardWho}>{whoMade(ch)}</Text>
              <Text style={styles.cardTime}>{stamp(ch.created_at)}</Text>
              <View style={{ flex: 1 }} />
              <Text style={[styles.status, { color: statusColor(ch.status), borderColor: statusColor(ch.status) }]}>{ch.status.toUpperCase()}</Text>
            </View>
            <Text style={styles.cardTitle} numberOfLines={2}>
              {ch.task_title ?? 'Task'}
            </Text>
            {describeChange(ch).map((line, i) => (
              <Text key={i} style={styles.cardLine}>
                {line}
              </Text>
            ))}
            {ch.note ? <Text style={styles.cardNote}>{ch.note}</Text> : null}
            {ch.status === 'pending' ? (
              <View style={styles.cardBtns}>
                <Pressable style={[styles.btn, styles.btnApprove]} disabled={!!busyId} onPress={() => act(ch, () => approveChange(ch))}>
                  <Check size={13} color={t.onGold} />
                  <Text style={styles.btnTextDark}>{busyId === ch.id ? '…' : 'Approve'}</Text>
                </Pressable>
                <Pressable style={[styles.btn, styles.btnGhost]} disabled={!!busyId} onPress={() => act(ch, () => (userId ? revertChange(userId, ch) : Promise.resolve('Not signed in')))}>
                  <Undo2 size={13} color={t.ivory} />
                  <Text style={styles.btnText}>Revert</Text>
                </Pressable>
                <Pressable style={[styles.btn, styles.btnGhost]} disabled={!!busyId} onPress={() => setEditing(ch)}>
                  <Pencil size={13} color={t.ivory} />
                  <Text style={styles.btnText}>Change</Text>
                </Pressable>
              </View>
            ) : null}
          </View>
        ))}
      </ScrollView>
    </View>
  );

  const historyPanel = (
    <View style={[styles.sheet, sheet]}>
      <View style={styles.sheetHead}>
        <Text style={styles.sheetTitle}>CONVERSATIONS</Text>
        {briefing ? (
          <Pressable
            style={[styles.btn, styles.btnApprove]}
            disabled={c.busy}
            onPress={() => {
              c.fresh();
              void talk();
            }}
          >
            <Plus size={13} color={t.onGold} />
            <Text style={styles.btnTextDark}>New balance</Text>
          </Pressable>
        ) : null}
      </View>
      <ScrollView contentContainerStyle={{ gap: 8, padding: 10 }}>
        {sessions.length === 0 ? <Text style={styles.empty}>No conversations yet.</Text> : null}
        {sessions.map((s) => {
          const n = changes.filter((x) => x.session_id === s.id).length;
          const open = s.id === c.sessionId;
          return (
            <Pressable
              key={s.id}
              style={[styles.card, open && { borderColor: t.goldBright }]}
              onPress={async () => {
                c.stop();
                await c.resume({ id: s.id, title: s.title, members: s.members, muted: s.muted ?? [], status: s.status ?? 'active', updated_at: s.updated_at });
                setPanel('chat');
              }}
            >
              <View style={styles.cardHead}>
                <Text style={styles.cardWho}>{stamp(s.created_at)}</Text>
                <View style={{ flex: 1 }} />
                <Text style={styles.cardTime}>
                  {s.lines} lines · {n} change{n === 1 ? '' : 's'}
                </Text>
              </View>
              {s.preview ? (
                <Text style={styles.cardLine} numberOfLines={2}>
                  {s.preview}
                </Text>
              ) : null}
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );

  return (
    <View style={styles.overlay} onLayout={(e) => setBox({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height })}>
      {W > 0 ? (
        <>
          {figure(LEFT, 'left')}
          {figure(RIGHT, 'right')}
          {panel === 'chat' ? (
            <>
              <CouncilTranscript c={shown} style={sheet} />
              <View style={{ position: 'absolute', left: wide ? sideL - 8 : 0, right: wide ? sideR - 8 : 0, bottom: 0, height: composerH + 40 }} pointerEvents="box-none">
                <CouncilComposer c={c} bottom={0} />
              </View>
            </>
          ) : panel === 'changes' ? (
            changesPanel
          ) : (
            historyPanel
          )}
          <Text style={styles.kicker}>BALANCE</Text>
          {/* Floating controls only (no chrome): close · stop · chat · changes · history */}
          <View style={styles.floatRow}>
            {c.busy ? (
              <Pressable onPress={c.stop} style={styles.float} hitSlop={6} accessibilityLabel="Stop them talking">
                <Square size={13} color={t.ivory} />
              </Pressable>
            ) : null}
            <Pressable onPress={() => setPanel('history')} style={[styles.float, panel === 'history' && styles.floatOn]} hitSlop={6} accessibilityLabel="Past conversations">
              <History size={15} color={panel === 'history' ? t.goldBright : t.ivory} />
            </Pressable>
            <Pressable onPress={() => { setPanel('changes'); void reloadChanges(); }} style={[styles.float, panel === 'changes' && styles.floatOn]} hitSlop={6} accessibilityLabel="Changes they made">
              <ListChecks size={15} color={panel === 'changes' ? t.goldBright : t.ivory} />
              {pending.length ? (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{pending.length}</Text>
                </View>
              ) : null}
            </Pressable>
            <Pressable onPress={() => setPanel('chat')} style={[styles.float, panel === 'chat' && styles.floatOn]} hitSlop={6} accessibilityLabel="The conversation">
              <MessageSquare size={15} color={panel === 'chat' ? t.goldBright : t.ivory} />
            </Pressable>
            <Pressable onPress={close} style={styles.float} hitSlop={6} accessibilityLabel="Close balance">
              <X size={16} color={t.ivory} />
            </Pressable>
          </View>

          {/* Change → Joel's own day + time (CLIO-style popup). */}
          {editing ? (
            <EditSchedule
              change={editing}
              onCancel={() => setEditing(null)}
              onSave={async (date, time) => {
                const ch = editing;
                setEditing(null);
                await act(ch, () => (userId ? editChangeSchedule(userId, ch, date, time) : Promise.resolve('Not signed in')));
              }}
            />
          ) : null}
        </>
      ) : null}
    </View>
  );
}

function EditSchedule({ change, onCancel, onSave }: { change: TaskChange; onCancel: () => void; onSave: (date: string, time: string) => void }) {
  const t = useV10();
  const styles = useStyles();
  const current = String(change.after?.scheduled ?? change.before?.scheduled ?? '');
  const m = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}))?/.exec(current);
  const [date, setDate] = useState(m?.[1] ?? ymd(new Date()));
  const [time, setTime] = useState(m?.[2] ?? '');
  const days = [0, 1, 2, 3, 4, 5, 6].map((n) => addDays(n));
  return (
    <View style={styles.popWrap}>
      <View style={styles.pop}>
        <Pressable onPress={onCancel} style={[styles.float, { position: 'absolute', top: 8, right: 8 }]} hitSlop={6} accessibilityLabel="Cancel">
          <X size={15} color={t.ivory} />
        </Pressable>
        <Text style={styles.popTitle} numberOfLines={2}>
          {change.task_title ?? 'Task'}
        </Text>
        <Text style={styles.popLabel}>Day</Text>
        <View style={styles.dayRow}>
          {days.map((d, i) => {
            const k = ymd(d);
            return (
              <Pressable key={k} onPress={() => setDate(k)} style={[styles.chip, date === k && styles.chipOn]}>
                <Text style={[styles.chipText, date === k && styles.chipTextOn]}>{i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}</Text>
              </Pressable>
            );
          })}
        </View>
        <TextInput value={date} onChangeText={setDate} placeholder="YYYY-MM-DD" placeholderTextColor="#6b7280" style={styles.popInput} />
        <Text style={styles.popLabel}>Time (optional)</Text>
        <View style={styles.dayRow}>
          {['', '08:00', '09:00', '12:00', '15:00', '18:00', '20:00'].map((tm) => (
            <Pressable key={tm || 'none'} onPress={() => setTime(tm)} style={[styles.chip, time === tm && styles.chipOn]}>
              <Text style={[styles.chipText, time === tm && styles.chipTextOn]}>{tm || 'Any time'}</Text>
            </Pressable>
          ))}
        </View>
        <TextInput value={time} onChangeText={setTime} placeholder="HH:MM" placeholderTextColor="#6b7280" style={styles.popInput} />
        <Pressable style={[styles.btn, styles.btnApprove, { alignSelf: 'flex-end', marginTop: 6 }]} onPress={() => onSave(date.trim(), time.trim())}>
          <Check size={14} color={t.onGold} />
          <Text style={styles.btnTextDark}>Save</Text>
        </Pressable>
      </View>
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    overlay: { ...StyleSheet.absoluteFillObject, zIndex: 20, backgroundColor: 'rgba(4,8,20,0.25)', borderRadius: 22, overflow: 'hidden' },
    kicker: { position: 'absolute', top: 12, left: 12, color: t.goldBright, fontSize: 12, fontWeight: '900', letterSpacing: 4, textShadowColor: 'rgba(0,0,0,0.9)', textShadowRadius: 6, zIndex: 6 },
    stand: { position: 'absolute', alignItems: 'center', zIndex: 3 },
    plate: { alignItems: 'center', marginTop: -6, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, backgroundColor: 'rgba(9,14,32,0.7)' },
    glow: { position: 'absolute', alignSelf: 'center', backgroundColor: t.goldGlow },
    name: { marginTop: 4, color: t.ivory, fontSize: 13, fontWeight: '900', letterSpacing: 3 },
    state: { color: t.ivoryFaint, fontSize: 11, fontStyle: 'italic' },
    floatRow: { position: 'absolute', top: 4, right: 6, flexDirection: 'row', gap: 8, zIndex: 7 },
    float: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(9,14,32,0.85)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.6)' },
    floatOn: { backgroundColor: 'rgba(214,180,106,0.28)', borderColor: t.goldBright, borderWidth: 2 },
    badge: { position: 'absolute', top: -5, right: -6, minWidth: 17, height: 17, borderRadius: 9, paddingHorizontal: 4, backgroundColor: t.goldBright, alignItems: 'center', justifyContent: 'center' },
    badgeText: { color: t.onGold, fontSize: 10, fontWeight: '900' },
    sheet: { position: 'absolute', borderRadius: 16, borderWidth: 1, borderColor: 'rgba(168,196,240,0.35)', zIndex: 5, overflow: 'hidden' },
    sheetHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', paddingHorizontal: 10, paddingTop: 10 },
    sheetTitle: { color: t.goldBright, fontSize: 12, fontWeight: '900', letterSpacing: 3 },
    bulkRow: { flexDirection: 'row', gap: 8, paddingHorizontal: 10, paddingTop: 8, flexWrap: 'wrap' },
    chip: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999, borderWidth: 1, borderColor: 'rgba(214,180,106,0.45)' },
    chipOn: { backgroundColor: t.gold, borderColor: t.gold },
    chipText: { color: t.ivorySoft, fontSize: 11.5, fontWeight: '700' },
    chipTextOn: { color: t.onGold },
    card: { gap: 4, padding: 10, borderRadius: 12, backgroundColor: 'rgba(27,42,78,0.85)', borderWidth: 1, borderColor: 'rgba(168,196,240,0.25)' },
    cardHead: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    cardWho: { color: t.ivory, fontSize: 12, fontWeight: '800' },
    cardTime: { color: t.ivoryFaint, fontSize: 11 },
    cardTitle: { color: t.ivory, fontSize: 14, fontWeight: '800' },
    cardLine: { color: t.ivorySoft, fontSize: 12.5 },
    cardNote: { color: t.ivoryFaint, fontSize: 11.5, fontStyle: 'italic' },
    cardBtns: { flexDirection: 'row', gap: 8, marginTop: 4, flexWrap: 'wrap' },
    status: { fontSize: 9.5, fontWeight: '900', letterSpacing: 1, borderWidth: 1, borderRadius: 999, paddingHorizontal: 7, paddingVertical: 2 },
    whoDot: { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(9,14,32,0.9)', borderWidth: 1, borderColor: t.gold },
    whoDotText: { color: t.goldBright, fontSize: 11, fontWeight: '900' },
    btn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 11, paddingVertical: 6, borderRadius: 999 },
    btnApprove: { backgroundColor: t.gold },
    btnGhost: { borderWidth: 1, borderColor: 'rgba(214,180,106,0.6)' },
    btnDanger: { backgroundColor: t.lapsed },
    btnText: { color: t.ivory, fontSize: 12, fontWeight: '800' },
    btnTextDark: { color: t.onGold, fontSize: 12, fontWeight: '800' },
    btnTextLight: { color: '#fff', fontSize: 12, fontWeight: '800' },
    empty: { color: t.ivoryFaint, fontStyle: 'italic', fontSize: 13, padding: 8 },
    err: { color: t.lapsed, fontSize: 12, paddingHorizontal: 10, paddingTop: 6 },
    popWrap: { ...StyleSheet.absoluteFillObject, zIndex: 30, backgroundColor: 'rgba(2,4,10,0.55)', alignItems: 'center', justifyContent: 'center', padding: 16 },
    pop: { width: '100%', maxWidth: 420, gap: 8, padding: 16, paddingTop: 18, borderRadius: 18, backgroundColor: '#1b2a4e', borderWidth: 1, borderColor: 'rgba(214,180,106,0.6)' },
    popTitle: { color: t.ivory, fontSize: 15, fontWeight: '900', paddingRight: 40 },
    popLabel: { color: t.goldBright, fontSize: 11, fontWeight: '900', letterSpacing: 2, marginTop: 4 },
    dayRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    popInput: { height: 40, borderRadius: 10, paddingHorizontal: 12, backgroundColor: '#ffffff', borderWidth: 1.5, borderColor: t.gold, color: '#111827', fontSize: 14 },
  }),
);

export default BalanceRoom;
